import { useRef, useState, type FormEvent } from 'react';
import { LsAction } from '@/components/ls-action/LsAction';
import { LsField } from '@/components/ls-field/LsField';
import {
  formatPhone,
  isAddressValid,
  isEmailValid,
  isFullNameValid,
  isPhoneComplete,
} from '@/shared/lib/contacts';
import styles from './ls-order-form.module.scss';
import { COPY } from '@/shared/content/copy';
import { DELIVERY_LABEL, DELIVERY_PRICE_RUB, type DeliveryMethod } from '@/shared/content/delivery';
import { formatRub } from '@/shared/lib/money';

export interface OrderFormValues {
  fullName: string;
  email: string;
  phone: string;
  comment: string;
  address: string;
  /** Доставка по России или самовывоз. Нет поля — доставка (прежние сборки). */
  deliveryMethod?: DeliveryMethod;
  /** Поле-ловушка: человек его не видит, робот заполняет. */
  trap: string;
  /** Сколько миллисекунд человек заполнял форму. */
  elapsedMs: number;
}

const EMPTY: OrderFormValues = {
  fullName: '',
  email: '',
  phone: '',
  comment: '',
  address: '',
  deliveryMethod: 'russia',
  trap: '',
  elapsedMs: 0,
};

type Errors = Partial<Record<keyof OrderFormValues, string>>;

interface Props {
  isSubmitting?: boolean;
  /** Подпись кнопки. По умолчанию «Оформить заказ»; где сразу платят — «Оплатить». */
  submitLabel?: string;
  /** Тянуться на ширину колонки, а не держать ширину макета (632). */
  isFluid?: boolean;
  /** Сообщить о выборе доставки — чтобы экран пересчитал итог. */
  onDeliveryChange?: (method: DeliveryMethod) => void;
  onSubmit: (values: OrderFormValues) => void;
}

/**
 * Форма заказа по макету: панель контактов из четырёх полей, панель адреса
 * с двухстрочным заголовком и кнопка во всю ширину панели.
 */
export function LsOrderForm({
  isSubmitting,
  submitLabel,
  isFluid,
  onDeliveryChange,
  onSubmit,
}: Props) {
  const [values, setValues] = useState<OrderFormValues>(EMPTY);
  const [errors, setErrors] = useState<Errors>({});
  const formRef = useRef<HTMLFormElement>(null);
  const openedAt = useRef(Date.now());

  const set = <K extends keyof OrderFormValues>(key: K, value: OrderFormValues[K]) =>
    setValues((previous) => ({ ...previous, [key]: value }));

  const validate = (current: OrderFormValues): Errors => {
    const next: Errors = {};

    if (!isFullNameValid(current.fullName)) next.fullName = COPY.order.errors.fullName;
    if (!isEmailValid(current.email)) next.email = COPY.order.errors.email;
    if (!isPhoneComplete(current.phone)) next.phone = COPY.order.errors.phone;
    // Адрес нужен только для доставки: самовывоз забирают у нас.
    if (current.deliveryMethod !== 'pickup' && !isAddressValid(current.address)) {
      next.address = COPY.order.errors.address;
    }

    setErrors(next);
    return next;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    // Значения берутся и из самих полей. Автозаполнение браузера иногда
    // показывает текст в поле, не сообщая о нём форме: на экране всё
    // заполнено, а проверка видела пустые поля и отказывала — с ошибками
    // у полей выше края прокрутки. Человеку казалось, что кнопка мертва.
    const current = withFieldValues(formRef.current, values);
    setValues(current);

    const next = validate(current);
    const first = FIELD_ORDER.find((key) => next[key]);
    if (!first) {
      onSubmit({ ...current, elapsedMs: Date.now() - openedAt.current });
      return;
    }

    // Отказ обязан быть виден: к первому неверному полю — прокрутка и курсор.
    const field = formRef.current?.querySelector<HTMLElement>(`[name="${first}"]`);
    field?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field?.focus({ preventScroll: true });
  };

  // По значениям, а не по ключам: сброшенная ошибка остаётся ключом с пустым значением.
  const hasErrors = Object.values(errors).some(Boolean);

  return (
    <form
      className={[styles['ls-order-form'], isFluid ? styles['ls-order-form--is-fluid'] : '']
        .join(' ')
        .trim()}
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate
    >
      {/*
        Поле-ловушка. Убрано из потока стилями, а не атрибутом hidden:
        роботы обычно пропускают скрытые атрибутом поля, но заполняют те,
        что просто не видны. Для читалок экрана поле скрыто явно, и в обход
        по Tab оно не попадает.
      */}
      <input
        className={styles['ls-order-form__trap']}
        type="text"
        name="company"
        value={values.trap}
        onChange={(event) => set('trap', event.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
      />

      <div className={styles['ls-order-form__panel']}>
        <LsField
          label={COPY.order.fields.fullName}
          value={values.fullName}
          error={errors.fullName}
          name="fullName"
          autoComplete="name"
          onChange={(event) => set('fullName', event.target.value)}
        />
        <LsField
          label={COPY.order.fields.email}
          type="email"
          inputMode="email"
          value={values.email}
          error={errors.email}
          name="email"
          autoComplete="email"
          onChange={(event) => set('email', event.target.value)}
        />
        <LsField
          label={COPY.order.fields.phone}
          type="tel"
          inputMode="tel"
          value={values.phone}
          error={errors.phone}
          name="phone"
          autoComplete="tel"
          onChange={(event) => set('phone', formatPhone(event.target.value))}
        />
        <LsField
          label={COPY.order.fields.comment}
          maxLength={500}
          name="comment"
          value={values.comment}
          onChange={(event) => set('comment', event.target.value)}
        />
      </div>

      <div className={styles['ls-order-form__panel']}>
        {/*
          Способ получения — выбором из двух, с ценой прямо на кнопке:
          человек видит, откуда в итоге берётся доставка, до оплаты.
        */}
        <div
          className={styles['ls-order-form__delivery']}
          role="radiogroup"
          aria-label="Способ получения"
        >
          {(['russia', 'pickup'] as const).map((method) => {
            const isOn = (values.deliveryMethod ?? 'russia') === method;
            const price = DELIVERY_PRICE_RUB[method];
            return (
              <button
                key={method}
                className={[
                  styles['ls-order-form__option'],
                  isOn ? styles['ls-order-form__option--is-on'] : '',
                ]
                  .join(' ')
                  .trim()}
                type="button"
                role="radio"
                aria-checked={isOn}
                onClick={() => {
                  set('deliveryMethod', method);
                  setErrors((previous) => ({ ...previous, address: undefined }));
                  onDeliveryChange?.(method);
                }}
              >
                <span className={styles['ls-order-form__option-title']}>
                  {DELIVERY_LABEL[method]}
                </span>
                <span className={styles['ls-order-form__option-price']}>
                  {price > 0 ? `+${formatRub(price)}` : 'бесплатно'}
                </span>
              </button>
            );
          })}
        </div>

        {values.deliveryMethod === 'pickup' ? (
          <p className={styles['ls-order-form__note']}>
            Заберёте заказ у нас в Москве. Адрес и время пришлём, когда заказ будет готов.
          </p>
        ) : (
          <LsField
            label={COPY.order.fields.address}
            value={values.address}
            error={errors.address}
            name="address"
            autoComplete="street-address"
            onChange={(event) => set('address', event.target.value)}
          />
        )}
      </div>

      {hasErrors && (
        <p className={styles['ls-order-form__summary']} role="alert">
          Проверьте поля, отмеченные выше
        </p>
      )}

      <div className={styles['ls-order-form__action']}>
        <LsAction
          label={isSubmitting ? COPY.order.submitting : (submitLabel ?? COPY.order.submit)}
          disabled={isSubmitting}
          onClick={() => formRef.current?.requestSubmit()}
        />
      </div>
    </form>
  );
}

/** Порядок полей сверху вниз — к первому неверному прокручиваем. */
const FIELD_ORDER = ['fullName', 'email', 'phone', 'address'] as const;

/**
 * Значения формы с учётом того, что стоит в самих полях.
 *
 * Поле с текстом важнее пустого значения в состоянии: так подхватывается
 * автозаполнение, о котором браузер форме не сообщил.
 */
function withFieldValues(form: HTMLFormElement | null, values: OrderFormValues): OrderFormValues {
  if (!form) return values;
  const read = (name: string): string => {
    const field = form.elements.namedItem(name);
    return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
      ? field.value
      : '';
  };
  // Берём то, что длиннее: в телефоне по умолчанию стоит «+7», и проверка
  // «пусто ли» считала его заполненным, пропуская номер из автозаполнения.
  const pick = (current: string, name: string): string => {
    const shown = read(name);
    return shown.trim().length > current.trim().length ? shown : current;
  };

  return {
    ...values,
    fullName: pick(values.fullName, 'fullName'),
    email: pick(values.email, 'email'),
    phone: formatPhone(pick(values.phone, 'phone')),
    comment: pick(values.comment, 'comment'),
    address: pick(values.address, 'address'),
  };
}
